import { gerarUuid } from "./gerar-uuid";
import type { ZodType } from "zod";
import { ErroDaApi } from "./erro-da-api";

export type OpcoesDaChamada = {
  readonly metodo?: "GET" | "POST" | "PATCH" | "DELETE";
  readonly corpo?: unknown;
  readonly consulta?: Readonly<Record<string, string | number | undefined>>;
  /** Chave de idempotencia. Obrigatoria em escrita, gerada aqui se omitida. */
  readonly chaveDeIdempotencia?: string;
  readonly sinal?: AbortSignal;
};

export type FonteDeToken = () => string | null;

export type RenovadorDeToken = () => Promise<string | null>;

/**
 * Cliente HTTP da aplicacao. Ponto unico de saida para a API.
 *
 * Concentra quatro coisas que, espalhadas, sempre divergem:
 *
 * 1. O token vai em TODA requisicao. Nao existe caminho de chamada que esqueca,
 *    porque nao existe outro caminho.
 * 2. Erro sempre vira `ErroDaApi` com o Problem Details preservado.
 * 3. Escrita ganha `Idempotency-Key` automaticamente — a API exige, e depender
 *    de cada tela lembrar produziria 422 intermitente.
 * 4. A resposta e validada com o schema Zod do contrato. Se a API mudar o
 *    formato, o erro aparece aqui com o campo, e nao como `undefined` no meio
 *    da renderizacao.
 */
export class ClienteHttp {
  private renovarToken?: RenovadorDeToken;

  constructor(
    private readonly urlBase: string,
    private readonly obterToken: FonteDeToken,
    private readonly buscar: typeof fetch = globalThis.fetch.bind(globalThis),
  ) {}

  configurarRenovacaoDeToken(renovar: RenovadorDeToken): void {
    this.renovarToken = renovar;
  }

  async requisitar<T>(
    caminho: string,
    schema: ZodType<T>,
    opcoes: OpcoesDaChamada = {},
    jaRenovou = false,
  ): Promise<T> {
    const metodo = opcoes.metodo ?? "GET";
    const token = this.obterToken();
    const cabecalhos: Record<string, string> = { accept: "application/json" };

    if (token) cabecalhos.authorization = `Bearer ${token}`;
    if (opcoes.corpo !== undefined) cabecalhos["content-type"] = "application/json";
    if (metodo !== "GET") {
      cabecalhos["idempotency-key"] =
        opcoes.chaveDeIdempotencia ?? gerarUuid();
    }

    let resposta: Response;
    try {
      resposta = await this.buscar(this.montarUrl(caminho, opcoes.consulta), {
        method: metodo,
        headers: cabecalhos,
        ...(opcoes.corpo !== undefined ? { body: JSON.stringify(opcoes.corpo) } : {}),
        ...(opcoes.sinal ? { signal: opcoes.sinal } : {}),
      });
    } catch (causa) {
      throw ErroDaApi.deRede(causa);
    }

    const texto = await resposta.text();
    const corpo = texto.length > 0 ? seguroJson(texto) : null;

    if (
      !resposta.ok &&
      resposta.status === 401 &&
      this.renovarToken &&
      !jaRenovou &&
      !caminho.includes("/v1/auth/refresh") &&
      !caminho.includes("/v1/auth/login")
    ) {
      const novoToken = await this.renovarToken();
      if (novoToken) {
        return this.requisitar(caminho, schema, opcoes, true);
      }
    }

    if (!resposta.ok) throw ErroDaApi.deResposta(resposta.status, corpo);

    const validado = schema.safeParse(corpo);
    if (!validado.success) {
      throw new ErroDaApi(
        resposta.status,
        null,
        `A resposta de ${caminho} não corresponde ao contrato: ${validado.error.issues
          .map((i) => `${i.path.join(".") || "(raiz)"} ${i.message}`)
          .join("; ")}`,
      );
    }
    return validado.data;
  }

  /**
   * Upload multipart (não define content-type JSON; o browser define o boundary).
   */
  async requisitarFormData<T>(
    caminho: string,
    schema: ZodType<T>,
    form: FormData,
    opcoes: Omit<OpcoesDaChamada, "corpo"> = {},
  ): Promise<T> {
    const token = this.obterToken();
    const cabecalhos: Record<string, string> = { accept: "application/json" };
    if (token) cabecalhos.authorization = `Bearer ${token}`;
    cabecalhos["idempotency-key"] =
      opcoes.chaveDeIdempotencia ?? gerarUuid();

    let resposta: Response;
    try {
      resposta = await this.buscar(this.montarUrl(caminho, opcoes.consulta), {
        method: "POST",
        headers: cabecalhos,
        body: form,
        ...(opcoes.sinal ? { signal: opcoes.sinal } : {}),
      });
    } catch (causa) {
      throw ErroDaApi.deRede(causa);
    }

    const texto = await resposta.text();
    const corpo = texto.length > 0 ? seguroJson(texto) : null;
    if (!resposta.ok) throw ErroDaApi.deResposta(resposta.status, corpo);
    const validado = schema.safeParse(corpo);
    if (!validado.success) {
      throw new ErroDaApi(
        resposta.status,
        null,
        `A resposta de ${caminho} não corresponde ao contrato`,
      );
    }
    return validado.data;
  }

  private montarUrl(
    caminho: string,
    consulta: OpcoesDaChamada["consulta"],
  ): string {
    const parametros = new URLSearchParams();
    for (const [chave, valor] of Object.entries(consulta ?? {})) {
      if (valor !== undefined && valor !== "") parametros.set(chave, String(valor));
    }
    const query = parametros.toString();
    return `${this.urlBase}${caminho}${query ? `?${query}` : ""}`;
  }
}

function seguroJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
}
